import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from 'designSystem/lib/utils'
import { Slot } from 'radix-ui'
import * as React from 'react'

function computeVisibleBadgeCount(
  measureEl: HTMLSpanElement,
  availableWidth: number,
  totalTags: number
): number {
  const children = Array.from(measureEl.children) as HTMLElement[]
  const overflowEl = children[children.length - 1]
  const overflowWidth = overflowEl?.scrollWidth ?? 0
  const gap = parseFloat(getComputedStyle(measureEl).gap) || 0

  let used = 0
  let count = 0

  for (let i = 0; i < children.length - 1; i++) {
    const width = children[i].scrollWidth
    const total = count === 0 ? width : used + gap + width
    const remaining = totalTags - (count + 1)
    const needed = remaining > 0 ? total + gap + overflowWidth : total

    if (needed > availableWidth) break
    used = total
    count++
  }

  return Math.max(count, 1)
}

const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'bg-destructive text-destructive-foreground focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90',
        outline:
          'border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        outlineMuted:
          'border-border text-muted-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        ghost: '[a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 [a&]:hover:underline',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

function Badge({
  className,
  variant = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span'

  return (
    <Comp
      data-slot='badge'
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

interface BadgeOverflowListProps {
  tags: string[]
  variant?: VariantProps<typeof badgeVariants>['variant']
}

function BadgeOverflowList({
  tags,
  variant = 'outlineMuted',
}: BadgeOverflowListProps) {
  const rowRef = React.useRef<HTMLSpanElement>(null)
  const measureRef = React.useRef<HTMLSpanElement>(null)
  const [visibleCount, setVisibleCount] = React.useState(tags.length)
  const tagsKey = tags.join('\0')

  React.useLayoutEffect(() => {
    const row = rowRef.current
    const measure = measureRef.current
    if (!row || !measure || tags.length === 0) {
      setVisibleCount(tags.length)
      return
    }

    const update = () => {
      const currentRow = rowRef.current
      const currentMeasure = measureRef.current
      if (!currentRow || !currentMeasure) return
      const width = currentRow.clientWidth
      if (width === 0) return
      setVisibleCount(
        computeVisibleBadgeCount(currentMeasure, width, tags.length)
      )
    }

    update()
    const observer = new ResizeObserver(update)
    observer.observe(row)
    return () => observer.disconnect()
  }, [tagsKey, tags.length])

  if (tags.length === 0) return null

  const overflowCount = tags.length - visibleCount

  return (
    <span
      data-slot='badge-overflow-list'
      className='relative min-w-0 flex-1 overflow-hidden'>
      <span
        ref={measureRef}
        aria-hidden='true'
        className='pointer-events-none invisible absolute top-0 left-0 flex gap-1'>
        {tags.map((tag) => (
          <Badge key={tag} variant={variant}>
            {tag}
          </Badge>
        ))}
        <Badge variant={variant} className='italic'>
          +{tags.length} more
        </Badge>
      </span>
      <span ref={rowRef} className='flex items-center gap-1 overflow-hidden'>
        {tags.slice(0, visibleCount).map((tag) => (
          <Badge key={tag} variant={variant}>
            {tag}
          </Badge>
        ))}
        {overflowCount > 0 && (
          <Badge variant={variant} className='italic'>
            +{overflowCount} more
          </Badge>
        )}
      </span>
    </span>
  )
}

export { Badge, BadgeOverflowList, badgeVariants }
